import { Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatUtcDateToIsoString, translate } from "../../Common";
import { TableColumn } from "../../models/Table";
import { IOSSwitch } from "../../theme/theme";
import { typography } from "../../theme/typography";
import SelectCell from "./SelectCell";
import UploadLogoModal from "./UploadLogoModal";


interface CellRendererProps {
  /**
   * Index of the row the data gets rendered to
    */
  rowIndex: number;
  /**
   * The value of the data to be rendered
   */
  cellData: any;
  /**
   * Column data (schema)
   */
  column: TableColumn;
  /**
   * Defines if row is in edit mode - renders cell data as a different component
   * (example: Select element instead of a text of the selected value) and defines if value editing is enabled.
   */
  isEditMode: boolean;
  /**
   * Defines if cell data of the row is rendered only in the expandable detail view.
   * Renders data like in "isEditMode" to also show its according column header.
   */
  isDetailPanel: boolean;
  /**
   * Object containing the fields (accessorKey) of the row where validation failed
   * when trying to save changes on an edited or new created row.
   */
  fieldErrors: { [key: string]: boolean };
  /**
   * Callback for onChange events
   * @param rowIndex - index from data set (row) being edited
   * @param columnName - property name of data being edited
   * @param value - new input value for property being edited
   */
  onCellValueChange: (rowIndex: number, accessorKey: string, value: any) => void;
}

/**
 * Defines custom cell renderers based on a column type from BE schema
 */
const CellRenderer: React.FC<CellRendererProps> = memo(({
  rowIndex,
  cellData,
  column,
  isEditMode,
  isDetailPanel,
  fieldErrors,
  onCellValueChange,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();

  const handleChange = React.useCallback((value) => {
    onCellValueChange(rowIndex, column.accessorKey, value);
  }, [rowIndex, column.accessorKey, onCellValueChange]);

  const handleOpenModal = React.useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleSaveLogos = React.useCallback((logos) => {
    handleChange(logos);
  }, [handleChange]);

  /**
   * Defines the type property for the <input> element.
   * @param schemaType - Type according to BE schema
   */
  const setTypeOfInput = (schemaType: string): string => {
    switch (schemaType) {
      case "Integer":
        return "number";
      case "Float":
        return "number";
      case "Text":
        return "text";
      case "ARRAY":
        return "text";
      case "DateTime":
        return "date";
      default:
        return schemaType; // should throw error on purpose if BE schema adds a new type that FE does not know
    }
  };

  /**
   * Returns comma separated and sorted string.
   * @param text - String array of values
   */
  const mergeAndSortMultiple = (text: string[]): string => {
    return text
      .filter((value) => column.options[value] !== undefined) // Filter out values not present in options
      .map((value) => column.options[value]) // Map values to their corresponding labels
      .sort((a, b) => a.localeCompare(b))
      .join(", ");
  };


  switch (column.type) {
    case "Text":
    case "Integer":
    case "ARRAY":
    case "Float":
      return isEditMode || isDetailPanel ? (
        // expanded row or expanded for edit mode
        <TextField
          disabled={!isEditMode || !column.isEditable}
          fullWidth
          label={column.header}
          variant="standard"
          size="small"
          type={setTypeOfInput(column.type)}
          error={fieldErrors[column.accessorKey]}
          value={cellData ?? ''}
          multiline={setTypeOfInput(column.type) === 'text'}
          maxRows={5}
          onChange={(event) => handleChange(event.target.value)}
          slotProps={{
            htmlInput: { maxLength: column.maxLength ? column.maxLength : undefined }
          }}
          sx={{ mb: isDetailPanel ? 2 : 0 }}
        />
      ) : (
        // Columns data when not expanded
        <Typography>{cellData ?? ''}</Typography>
      );

    case "DateTime":
      // expanded row or expanded for edit mode
      return isEditMode || isDetailPanel ? (
        <TextField
          disabled={!isEditMode || !column.isEditable}
          fullWidth
          label={column.header}
          variant="standard"
          size="small"
          // Render DateTime schema types only as HTML Date input when it's editable
          type={column.isEditable ? "date" : "text"}
          error={fieldErrors[column.accessorKey]}
          // Adjust date formatting display to input type "date" or "text": Not editable dates should be rendered with time.
          // Render null dates with empty string (required for type="date" inputs).
          value={cellData ? (column.isEditable ? formatUtcDateToIsoString(cellData) : formatDate(cellData)) : ''}
          onChange={(event) => handleChange(event.target.value)}
          sx={{ mb: isDetailPanel ? 2 : 0 }}
        />
      ) : (
        // Columns data when not expanded: Render date as readable string
        <Typography>{formatDate(cellData)}</Typography>
      );

    case "Enum":
      // expanded row or expanded for edit mode: Render array as Select
      return isEditMode || isDetailPanel ? (
        <Box sx={{ mb: isDetailPanel ? 2 : 0 }}>
          <SelectCell
            disabled={!isEditMode || !column.isEditable}
            label={column.header}
            value={cellData}
            options={column.options || []}
            error={fieldErrors[column.accessorKey]}
            onChange={(value) => handleChange(value)}
            nullable={column.nullable}
            accessorKey={column.accessorKey}
          />
        </Box>
      ) : (
        // Columns data when not expanded: Render the selected option
        <Typography>{translate(t, column.accessorKey, cellData)}</Typography>
      );

    case "UUID":
      // expanded row or expanded for edit mode: Render Map as Select
      return isEditMode || isDetailPanel ? (
        <Box sx={{ mb: isDetailPanel ? 2 : 0 }}>
          <SelectCell
            disabled={!isEditMode || !column.isEditable}
            label={column.header}
            value={cellData}
            options={column.options || []}
            error={fieldErrors[column.accessorKey]}
            onChange={(value) => handleChange(value)}
            nullable={column.nullable}
            multiple={column.accessorKey === "user_roles" || column.accessorKey === "client_carriers" || column.accessorKey === "needs_mail_confirmation_to_clients" || column.accessorKey === "needs_mail_confirmation_to_carriers"}
            accessorKey={column.accessorKey}
          />
        </Box>
      ) : (
        // Columns data when not expanded: Render the selected option
        <>
          {column.accessorKey === "user_roles" || column.accessorKey === "client_carriers" || column.accessorKey === "needs_mail_confirmation_to_clients" || column.accessorKey === "needs_mail_confirmation_to_carriers" ? ( // data that has multiple selection
            <Typography>{mergeAndSortMultiple(cellData)}</Typography>
          ) : (
            <Typography>
              {translate(
                t,
                column.accessorKey,
                column.accessorKey === "claim_status_id"
                  ? cellData
                  : column.options[cellData],
              )}
            </Typography>
          )}
        </>
      );

    case "JSON":
      // Reserved for client and carrier logo - position in expanded defined via backend response order
      return (
        <Box sx={{ pt: 2, pb: 2 }}>
          <Typography sx={{ ...typography.titleMd, pr: 2, pb: 2 }}>
            {column.header}
          </Typography>
          <span
            style={{ marginRight: "16px" }}
            dangerouslySetInnerHTML={{ __html: cellData?.light_logo }}
          />
          <span dangerouslySetInnerHTML={{ __html: cellData?.dark_logo }} />
          <Typography
            style={{
              ...typography.titleMd,
              display: "block",
              marginTop: "16px",
              textDecoration: "underline",
              color: !isEditMode ? "gray" : "#47BCE5",
              cursor: !isEditMode ? "default" : "pointer",
            }}
            onClick={!isEditMode ? undefined : handleOpenModal}
          >
            {(cellData?.light_logo && cellData?.dark_logo) ? t("TABLE.EDIT_LOGOS") : t("TABLE.UPLOAD_LOGOS")}
          </Typography>

          <UploadLogoModal
            cellData={cellData}
            open={openModal}
            onClose={handleCloseModal}
            onSave={handleSaveLogos}
          />
        </Box>
      );

    case "Boolean":
      // Render Toggle Switches - add headline for expanded row
      return (
        <>
          {isDetailPanel && (
            <Typography variant="bodyMd">{column.header}</Typography>
          )}
          <IOSSwitch
            sx={{ ml: isDetailPanel ? "20px" : "0" }}
            disabled={!isEditMode || !column.isEditable}
            checked={cellData ?? false}
            onChange={(event) => handleChange(event.target.checked)}
          />
        </>
      );

    case "Object":
      // During editing, treat as pure string
      // Only parse when saving (when blur occurs)
      // For display, check if it's already a string or needs to be stringified
      const stringifiedValue = typeof cellData === 'string' 
        ? cellData 
        : (cellData ? JSON.stringify(cellData, null, 2) : '');
      
      return isEditMode || isDetailPanel ? (
        <TextField
          disabled={!isEditMode || !column.isEditable}
          fullWidth
          label={column.header}
          variant="standard"
          size="small"
          multiline
          maxRows={8}
          minRows={3}
          error={fieldErrors[column.accessorKey]}
          value={stringifiedValue}
          onChange={(event) => {
            // Store the raw input value as a string without attempting to parse
            // This prevents conversion issues during typing
            handleChange(event.target.value);
          }}
          onBlur={(event) => {
            // Only on blur (when user is done editing), try to parse the string to JSON object
            const inputValue = event.target.value.trim();

            if (inputValue === '' || inputValue === '{}' || inputValue === '[]') {
              handleChange(null);
              return;
            }

            try {
              // Parse the string back to an object only when saving/leaving the field
              const parsedValue = JSON.parse(inputValue);
              handleChange(parsedValue);
            } catch (error) {
              // If parsing fails, keep it as a string (don't modify)
              console.error("Invalid JSON format:", error);
              // Just leave the string value as is
            }
          }}
          onKeyDown={(event) => {
            // Handle Ctrl+A + Delete to clear and set null
            if (event.ctrlKey && event.key === 'a') {
              // Let Ctrl+A work normally, but prepare for potential delete
              return;
            }

            // If all text is selected and delete/backspace is pressed, clear to null
            if ((event.key === 'Delete' || event.key === 'Backspace')) {
              const target = event.target as HTMLInputElement;
              const isAllSelected = target.selectionStart === 0 && target.selectionEnd === target.value.length;

              if (isAllSelected && target.value.length > 0) {
                event.preventDefault();
                handleChange(null);
                target.value = '';
              }
            }
          }}
          slotProps={{
            input: {
              style: {
                fontFamily: 'monospace', // Use monospace font for better JSON readability
                fontSize: '0.875rem'
              }
            }
          }}
          sx={{ mb: isDetailPanel ? 2 : 0 }}
        />
      ) : (
        // Display formatted string in collapsed view with truncation
        // Don't try to parse or manipulate it as JSON - just display it as a string
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            fontFamily: 'monospace' // Use monospace for consistency
          }}
          title={cellData ? String(cellData) : ''} // Show full string on hover
        >
          {cellData ? String(cellData) : ''}
        </Typography>
      );

    default:
      return null;
  }
});

export default memo(CellRenderer, (prevProps, nextProps) => {
  // Only re-render if these props have changed
  return (
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.cellData === nextProps.cellData &&
    prevProps.column.accessorKey === nextProps.column.accessorKey &&
    prevProps.isEditMode === nextProps.isEditMode &&
    prevProps.isDetailPanel === nextProps.isDetailPanel &&
    (prevProps.fieldErrors?.[prevProps.column.accessorKey] === nextProps.fieldErrors?.[nextProps.column.accessorKey])
  );
});
